import type { BrandSpecials } from '@iheartjane/data-access';
import { SmartSort } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import {
  INITIAL_WIZARD_FORM_DATA,
  WizardError,
  WizardFloatingButton,
  WizardLoading,
  WizardModal,
  WizardNoResults,
  WizardResultCard,
  WizardResults,
  WizardSelection,
  WizardWelcome,
  productPhotoMetadata,
} from '@iheartjane/menu';
import type {
  WizardFormData,
  WizardProps,
  WizardStepKey,
} from '@iheartjane/menu';
import { Button, JaneGoldIcon, Link, Tag } from '@iheartjane/reefer';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouteLoaderData, useSubmit } from 'react-router';

import { BagTransferContent } from '@jane/bloom/components/bagTransferModal/bagTransferContent';
import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { ProductCategoryLabel } from '@jane/bloom/components/productCategoryLabel/productCategoryLabel';
import { WizardAddToCartButton } from '@jane/bloom/components/wizardAddToCartButton/wizardAddToCartButton';
import { useBagTransfer } from '@jane/bloom/hooks/cart/useBagTransfer';
import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import type { AddToCartFormArgs } from '@jane/bloom/hooks/useAddToCartConfig';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useWizardResultsFetcher } from '@jane/bloom/hooks/useWizardResultsFetcher';
import { bloomContrastColorKey } from '@jane/bloom/resources/theme';
import type { LoaderData } from '@jane/bloom/routes/_menu/types';
import { WIZARD_SOURCE } from '@jane/bloom/services/tracking/Events';
import type { WizardOpenSource } from '@jane/bloom/services/tracking/Events';
import {
  trackAbTest,
  trackProductListingTap,
  trackWizardOpened,
  trackWizardResultsViewed,
  trackWizardSelectionConfirmed,
} from '@jane/bloom/services/tracking/track';
import type { StoreSpecials } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { getGoldLabel } from '@jane/bloom/utils/badges/getGoldLabel';
import { getSpecialLabel } from '@jane/bloom/utils/badges/getSpecialLabel';
import { getShouldShowGold } from '@jane/bloom/utils/gold/getShouldShowGold';
import { getProductPriceLabels } from '@jane/bloom/viewModels/getPrice';
import { buildSingleProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearchResult';
import type { AlgoliaProduct } from '@jane/search/types';

import styles from './bloomWizardModal.module.css';

type WizardPhase =
  | 'bag-transfer'
  | 'error'
  | 'filter-selection'
  | 'loading'
  | 'no-results'
  | 'results'
  | 'welcome';

interface BloomWizardModalProps {
  /**
   * @todo Remove this prop once brandSpecials is consolidated into the _menu
   * loader alongside janeGold. Then it can be accessed via useRouteLoaderData
   * like goldConfig and userIdentity already are.
   */
  brandSpecials?: BrandSpecials;
  children: (args: {
    openModal: (source: WizardOpenSource) => void;
  }) => ReactNode;
  specials?: StoreSpecials;
}

function getSelectedFilterNames(formData: WizardFormData): string[] {
  return Object.entries(formData)
    .filter(([, values]) => values.length > 0)
    .map(([key]) => key);
}

function formatRatingSummary(
  rating?: number | null,
  count?: number | null
): string | null {
  if (!rating || rating < 1) return null;
  const rounded = rating.toFixed(1);
  return count ? `${rounded} (${count})` : rounded;
}

export function BloomWizardModal({
  brandSpecials = [],
  children,
  specials,
}: BloomWizardModalProps) {
  const generatePath = useFullDestination();
  const submit = useSubmit();
  const { cart, customLanguage, featureFlags, logo, pinnedStore, theme } =
    useRootLoaderData();
  const { fulfillmentMode, searchReservationMode } = useStoreSwitcher();
  const menuLayoutData = useRouteLoaderData<LoaderData>('routes/_menu');
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<WizardFormData>(
    INITIAL_WIZARD_FORM_DATA
  );
  const [wizardPhase, setWizardPhase] = useState<WizardPhase>('welcome');
  const [pendingFormArgs, setPendingFormArgs] =
    useState<AddToCartFormArgs | null>(null);

  const { cancelTransfer, checkAndHandleTransfer, confirmTransfer, newCart } =
    useBagTransfer();
  const hasTrackedResults = useRef(false);
  const showFloatingButton = Boolean(
    featureFlags[FLAGS.obWizardBannerEnabled] &&
    featureFlags[FLAGS.c2WizardFloatingButton]
  );
  const showWizardBanner = Boolean(featureFlags[FLAGS.obWizardBannerEnabled]);

  const handleGetStarted = () => {
    setWizardPhase('filter-selection');
    setFormData({ ...INITIAL_WIZARD_FORM_DATA });
  };

  const {
    isError,
    isLoading,
    isSuccess,
    submit: submitWizard,
    wizardResults,
  } = useWizardResultsFetcher({
    formData,
    storeId: pinnedStore?.id ?? 0,
  });

  const handleComplete = () => {
    hasTrackedResults.current = false;
    setWizardPhase('loading');
    submitWizard();
  };

  const handleStep = useCallback(
    (wizardStep: WizardStepKey, form: WizardFormData) => {
      trackWizardSelectionConfirmed(wizardStep, form[wizardStep]);
    },
    []
  );

  const products = useMemo(() => {
    if (!wizardResults?.recommendedSortResults) return [];

    const instance = SmartSort.fromJSON<AlgoliaProduct>(
      wizardResults.recommendedSortResults
    );
    return instance.products;
  }, [wizardResults]);

  const janeGold = menuLayoutData?.janeGold;

  useEffect(() => {
    if (!pinnedStore || !showWizardBanner) return;
    trackAbTest({
      enabled: showFloatingButton,
      experiment: 'Wizard Floating Button',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWizardBanner, showFloatingButton]);

  useEffect(() => {
    if (wizardPhase !== 'loading') return;
    if (isLoading) return;
    if (!isError && !isSuccess) return; // Submit hasn't completed yet

    if (isError) {
      setWizardPhase('error');
    } else if (products.length === 0) {
      setWizardPhase('no-results');
    } else {
      setWizardPhase('results');
    }
  }, [wizardPhase, isError, isSuccess, isLoading, products.length]);

  useEffect(() => {
    if (wizardPhase === 'results' && !hasTrackedResults.current) {
      hasTrackedResults.current = true;
      trackWizardResultsViewed({
        appliedFilters: wizardResults?.appliedFilters ?? [],
        selectedFilters: getSelectedFilterNames(formData),
        skippedFilters: wizardResults?.skippedFilters ?? [],
        totalProducts: products.length,
      });
    }
  }, [formData, products, wizardPhase, wizardResults]);

  const handleEdit = () => {
    setWizardPhase('filter-selection');
  };

  const handleTryAgain = () => {
    hasTrackedResults.current = false;
    setWizardPhase('loading');
    submitWizard();
  };

  const handleClose = () => {
    setIsOpen(false);
    setWizardPhase('welcome');
  };

  const handleTransferConfirm = () => {
    confirmTransfer(() => {
      if (pendingFormArgs) {
        submit({ shouldReplaceCart: 'true' }, pendingFormArgs);
      }
      setWizardPhase('results');
      setPendingFormArgs(null);
    });
  };

  const handleTransferCancel = () => {
    cancelTransfer();
    setWizardPhase('results');
    setPendingFormArgs(null);
  };

  if (!pinnedStore) {
    return <>{children({ openModal: (_source: WizardOpenSource) => {} })}</>;
  }

  const wizardProps: WizardProps = {
    customProductTypeLabels:
      pinnedStore.custom_product_type_labels ?? undefined,
    disabledCategories: menuLayoutData?.disabledWizardCategories ?? [],
    disabledFeelingTags: pinnedStore.disabled_feeling_and_activity_tags ?? [],
    storeState: pinnedStore.state,
  };

  const editAndStartOverActions = (
    <div className={styles['editAndStartOverActions']}>
      <Link onClick={handleEdit}>Edit</Link>
      <Link onClick={handleGetStarted}>Start over</Link>
    </div>
  );

  const startOverAction = <Link onClick={handleGetStarted}>Start over</Link>;

  const headerActionsMap: Partial<Record<WizardPhase, ReactNode>> = {
    'bag-transfer': editAndStartOverActions,
    error: editAndStartOverActions,
    loading: startOverAction,
    'no-results': startOverAction,
    results: editAndStartOverActions,
  };

  const openModal = (source: WizardOpenSource) => {
    trackWizardOpened(source);
    setIsOpen(true);
  };

  return (
    <>
      {children({ openModal })}
      {showFloatingButton && (
        <WizardFloatingButton
          iconColor={bloomContrastColorKey(
            theme?.bloom?.colors?.primary?.contrast
          )}
          onClickStart={() => openModal(WIZARD_SOURCE.BUTTON)}
        />
      )}
      <ClientOnly>
        {() => (
          <WizardModal
            headerActions={headerActionsMap[wizardPhase]}
            isOpen={isOpen}
            onClose={handleClose}
            logoUrl={logo?.small}
            headerColor={theme?.bloom?.colors?.header?.main ?? ''}
          >
            {wizardPhase === 'welcome' && (
              <WizardWelcome onGetStarted={handleGetStarted} />
            )}
            {wizardPhase === 'filter-selection' && (
              <WizardSelection
                form={formData}
                onStep={handleStep}
                updateForm={setFormData}
                wizardProps={wizardProps}
                onComplete={handleComplete}
              />
            )}
            {wizardPhase === 'loading' && (
              <WizardLoading onCancel={handleEdit} />
            )}
            {wizardPhase === 'no-results' && (
              <WizardNoResults onEditAnswers={handleEdit} />
            )}
            {wizardPhase === 'error' && (
              <WizardError onTryAgain={handleTryAgain} />
            )}
            {wizardPhase === 'bag-transfer' && (
              <BagTransferContent
                newBag={newCart}
                onCancel={handleTransferCancel}
                onConfirm={handleTransferConfirm}
                variant="wizard"
              />
            )}
            {wizardPhase === 'results' && (
              <WizardResults>
                {products.map((product, i) => {
                  const { attributes } = product;
                  const productId = String(attributes.product_id);
                  const bullets =
                    wizardResults?.bulletsByProductId[productId] ?? [];
                  const { discountedPriceLabel, priceLabel } =
                    getProductPriceLabels(attributes);
                  const displayPrice = discountedPriceLabel ?? priceLabel;
                  const originalPrice =
                    discountedPriceLabel && discountedPriceLabel !== priceLabel
                      ? priceLabel
                      : undefined;
                  const hasMultipleWeights =
                    attributes.available_weights.length > 1;

                  const specialLabel = getSpecialLabel({
                    bundleTag: Boolean(featureFlags[FLAGS.c1BundleTag]),
                    product: attributes,
                    specials: specials ?? null,
                    storeComplianceLanguageSettings:
                      customLanguage.storeComplianceLanguageSettings,
                  });

                  const shouldShowGold = Boolean(
                    pinnedStore &&
                    janeGold &&
                    getShouldShowGold({
                      goldConfig: janeGold.config,
                      product: attributes,
                      store: pinnedStore,
                      userSegments: janeGold.userIdentity?.user_segments,
                    })
                  );

                  const goldLabel =
                    shouldShowGold &&
                    attributes.applicable_brand_special_ids?.length
                      ? getGoldLabel({
                          applicableBrandSpecialIds:
                            attributes.applicable_brand_special_ids,
                          brandSpecials,
                          lowestAvailablePriceVal:
                            attributes.lowest_available_price?.val,
                        })
                      : null;

                  const productPath = generatePath(
                    `products/${productId}/${attributes.unique_slug}`
                  );

                  const productViewModel =
                    buildSingleProductSearchResultViewModel({
                      cart,
                      customLanguage,
                      pinnedStore,
                      product: attributes,
                      specials: specials ?? null,
                    });

                  return (
                    <WizardResultCard
                      key={productId}
                      onImpress={() => product.impress()}
                      onClick={() => {
                        product.click();
                        trackProductListingTap(
                          {
                            cardLocation: 'wizard',
                            fulfillmentMode,
                            index: i,
                            product: productViewModel,
                            reservationMode: searchReservationMode,
                            storeId: pinnedStore?.id,
                          },
                          featureFlags
                        );
                        window.open(
                          productPath,
                          '_blank',
                          'noreferrer noopener'
                        );
                      }}
                      actionSlot={
                        hasMultipleWeights ? (
                          <div style={{ display: 'block' }}>
                            <Button
                              full
                              href={productPath}
                              label="Select weight"
                              onClick={(e) => {
                                e.stopPropagation();
                                product.click();
                              }}
                              target="_blank"
                              variant="primary"
                            />
                          </div>
                        ) : (
                          <WizardAddToCartButton
                            checkAndHandleTransfer={checkAndHandleTransfer}
                            index={i}
                            onTransferTriggered={(transferFormArgs) => {
                              setPendingFormArgs(transferFormArgs);
                              setWizardPhase('bag-transfer');
                            }}
                            product={productViewModel}
                            smartProductInstance={product}
                          />
                        )
                      }
                      brand={attributes.brand ?? ''}
                      bullets={bullets}
                      categoryLabel={
                        <ProductCategoryLabel
                          category={attributes.category}
                          kind={attributes.kind}
                        />
                      }
                      imageUrl={productPhotoMetadata(attributes).url}
                      isSponsored={product.isSponsored}
                      isStaffPick={i === 2}
                      name={attributes.name}
                      price={displayPrice}
                      priceOriginal={originalPrice}
                      ratingSummary={formatRatingSummary(
                        attributes.aggregate_rating,
                        attributes.review_count
                      )}
                      tags={
                        goldLabel || specialLabel ? (
                          <>
                            {goldLabel && (
                              <Tag
                                background="gold"
                                color="grays-black"
                                icon={<JaneGoldIcon size="sm" />}
                                label={goldLabel}
                                size="sm"
                              />
                            )}
                            {specialLabel && (
                              <Tag
                                background="info"
                                color="text-info"
                                label={specialLabel}
                                size="sm"
                              />
                            )}
                          </>
                        ) : undefined
                      }
                    />
                  );
                })}
              </WizardResults>
            )}
          </WizardModal>
        )}
      </ClientOnly>
    </>
  );
}
